
import { Component, Prop, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteRepositoryInterface, {
  Website,
  WebsiteCreationActionStatus, WebsiteCreationStatus,
  WebsiteDeletionAction,
} from '@/services/Website/interfaces';
import WebsiteMigrationsRepositoryInterface, {
  OrchestratorActionStatus,
  OrchestratorActionType,
  WebsiteMigration,
  WebsiteMigrationAction,
  WebsiteMigrationActionMessageInterface,
  WebsiteMigrationActionMessagePayloadInterface,
  WebsiteMigrationMessageInterface,
} from '@/services/WebsiteMigrations/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import WebsiteGroupRepositoryInterface, { WebsiteGroup } from '@/services/WebsiteGroup/interfaces';
import UpdateWebsiteActionStatusDialog from '@/components/dialogs/website/UpdateWebsiteActionStatusDialog.vue';
import VComponent from '@/components/base/VComponent.vue';
import RetryWebsiteCreationActionDialog from '@/components/dialogs/website/RetryWebsiteCreationActionDialog.vue';
import IgnoreWebsiteCreationActionDialog from '@/components/dialogs/website/IgnoreWebsiteCreationActionDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import StatusChip from '@/components/layout/StatusChip.vue';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'websiteRepository',
    'websiteGroupRepository',
    'websiteMigrationsRepository',
    'serverRepository',
  ],
  components: {
    VComponent,
    UpdateWebsiteActionStatusDialog,
    RetryWebsiteCreationActionDialog,
    IgnoreWebsiteCreationActionDialog,
    PageHeader,
    StatusChip,
  },
  sockets: {
    websiteDeletion(msg: { payload: WebsiteDeletionAction[] }): void {
      this.$emit('websiteDeleting', msg.payload);
    },
    websiteMigration(msg: WebsiteMigrationMessageInterface): void {
      this.$emit('migrationReceived', msg);
    },
    websiteMigrationAction(msg: WebsiteMigrationActionMessageInterface): void {
      this.$emit('actionReceived', msg.payload);
    },
  },
})
export default class ViewServerMigration extends Vue {
  @Prop({ required: true }) public migration!: WebsiteMigration;

  public loading: boolean = true;

  public actionsMap: { [key: string]: string } = {
    'migrate_dns': 'MIGRATE DNS',
    'delete_dns': 'DELETE SOURCE DNS',
    'migrate_unix_user': 'MIGRATE UNIX USER',
    'delete_unix_user': 'DELETE SOURCE UNIX USER',
    'migrate_database': 'MIGRATE DATABASE',
    'delete_database': 'DELETE SOURCE DATABASE',
    'migrate_vhost': 'MIGRATE VHOST',
    'delete_vhost': 'DELETE SOURCE VHOST',
    'migrate_redis_cache': 'MIGRATE REDIS CACHE',
    'delete_redis_cache': 'DELETE REDIS CACHE',
    'migrate_email': 'MIGRATE EMAIL',
    'delete_email': 'DELETE SOURCE EMAIL',
    'migrate_letsencrypt': 'MIGRATE SSL',
    'delete_letsencrypt': 'DELETE SOURCE SSL',
    'migrate_cdn': 'MIGRATE CDN',
    'deactivate_cdn': 'DELETE SOURCE CDN',
    'migrate_ftp': 'MIGRATE FTP',
    'delete_ftp': 'DELETE SOURCE FTP',
    'approve_migration_data': 'APPROVE MIGRATION DATA',
    'approve_website_migration': 'APPROVE WEBSITE MIGRATION',
  };

  public actionsStatusMap: { [key: string]: string } = {
    'pending': 'PENDING',
    'in progress': 'IN PROGRESS',
    'failed': 'FAILED',
    'retry': 'RETRY',
    'retrying': 'RETRYING',
    'done': 'DONE',
    'rolling back': 'ROLLING BACK',
    'rolled back': 'ROLLED BACK',
    'cancelled': 'CANCELLED',
  };

  public actionStatusIconMap: { [key: string]: string } = {
    'pending': 'mdi-progress-clock',
    'in progress': 'mdi-progress-helper',
    'failed': 'mdi-close-thick',
    'retry': 'mdi-repeat',
    'retrying': 'mdi-repeat-variant',
    'done': 'mdi-check',
    'rolling back': 'mdi-arrow-u-left-top',
    'rolled back': 'mdi-backup-restore',
    'cancelled': 'mdi-cancel',
  };

  public reloadActions: string[] = [
    'migrate_vhost',
    'migrate_letsencrypt',
    'migrate_unix_user',
    'migrate_database',
  ];

  public websiteRepository!: WebsiteRepositoryInterface;

  public serverRepository!: ServerRepositoryInterface;

  public websiteGroupRepository!: WebsiteGroupRepositoryInterface;

  public websiteMigrationsRepository!: WebsiteMigrationsRepositoryInterface;

  public sourceWebsite: Website = {} as Website;

  public sourceGroup: WebsiteGroup = {} as WebsiteGroup;

  public targetWebsite: Website = {} as Website;

  public targetGroup: WebsiteGroup = {} as WebsiteGroup;

  public migrationActionsMap: { [key: number]: string } = {};

  public serversMap: { [key: string]: string } = {};

  public deletionActionsMap: { [key: number]: string } = {};

  public deletionActions: WebsiteDeletionAction[] = [];

  public updateWebsiteActionStatusDialog: string = '';

  public statusForUpdateWebsiteActionStatusDialog: string = '';

  public actionForUpdateActionStatusDialog: WebsiteMigrationAction = {} as WebsiteMigrationAction;

  public retryWebsiteActionDialog: string = '';

  public domainForRetryWebsiteActionDialog: string = '';

  public creationIdForRetryWebsiteActionDialog: number = -1;

  public actionForRetryWebsiteActionDialog: WebsiteMigrationAction = {} as WebsiteMigrationAction;

  public ignoreWebsiteActionDialog: string = '';

  public domainForIgnoreWebsiteActionDialog: string = '';

  public creationIdForIgnoreWebsiteActionDialog: number = -1;

  public actionForIgnoreWebsiteActionDialog: WebsiteMigrationAction = {} as WebsiteMigrationAction;

  public async created(): Promise<void> {
    this.$on('websiteDeleting', this.handleWebsiteDeleting);
    this.$on('migrationReceived', this.handleMigrationMessage);
    this.$on('actionReceived', this.handleWSActionMessage);

    await this.initialize();
  }

  public async initialize(): Promise<void> {
    try {
      this.setMigrationActionsMap();
      const websites = await this.websiteRepository.getWebsitesByIds([
        this.migration.sourceWebsiteId,
        this.migration.targetWebsiteId,
      ]);

      const serversIds: string[] = [];
      const sourceWebsite = websites.find((website) => website.id === this.migration.sourceWebsiteId);
      if (sourceWebsite) {
        this.sourceWebsite = sourceWebsite;

        serversIds.push(sourceWebsite.originServer);

        this.sourceGroup = await this.websiteGroupRepository.getGroupByWebsite(sourceWebsite.id);
      }

      const targetWebsite = websites.find((website) => website.id === this.migration.targetWebsiteId);
      if (targetWebsite) {
        this.targetWebsite = targetWebsite;

        if (!serversIds.includes(targetWebsite.originServer)) {
          serversIds.push(targetWebsite.originServer);
        }

        this.targetGroup = await this.websiteGroupRepository.getGroupByWebsite(targetWebsite.id);
      }

      if (serversIds.length) {
        const servers = await this.serverRepository.getServersByIds(serversIds);
        servers.forEach((server) => {
          this.serversMap[server._id] = server.hostname;
        });
      }

      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(
        `Error occurred while fetching migration data, ${err?.response?.data?.error || err.message}`,
      );
    }
  }

  public setMigrationActionsMap(): void {
    this.migration.actions.forEach((action) => {
      this.migrationActionsMap[action.id] = action.status;
    });
  }

  public handleWebsiteDeleting(deletionActions: WebsiteDeletionAction[]): void {
    if (deletionActions[0].deletion.websiteId !== this.sourceWebsite?.id) {
      return;
    }

    deletionActions.forEach((deletionAction) => {
      this.deletionActionsMap[deletionAction.id] = deletionAction.status;
    });

    this.deletionActions = deletionActions;
  }

  public handleMigrationMessage(msg: WebsiteMigrationMessageInterface): void {
    if (this.migration.id !== msg.payload.id) {
      return;
    }

    this.migration.status = msg.payload.status;
  }

  public handleWSActionMessage(action: WebsiteMigrationActionMessagePayloadInterface): void {
    if (this.migrationActionsMap[action.id]) {
      const actionIndex = this.migration.actions.findIndex((migrationAction) => migrationAction.id === action.id);
      if (actionIndex === -1) {
        return;
      }

      this.migration.actions[actionIndex].status = action.status;

      if (this.reloadActions.includes(this.migration.actions[actionIndex].action)) {
        this.reloadTargetWebsiteData();
      }

      if (!action.logs || !action.logs.length) {
        return;
      }

      if (!this.migration.actions[actionIndex].logs) {
        this.migration.actions[actionIndex].logs = [];
      }

      this.migration.actions[actionIndex].logs.push(...action.logs.map((l) => ({
        id: -1,
        log: l,
        createdDate: (new Date()).toISOString(),
      })));

      return;
    }

    if (this.deletionActionsMap[action.id]) {
      const index = this.deletionActions.findIndex((deletionAction) => deletionAction.id === action.id);
      if (index === -1) {
        return;
      }

      this.deletionActions[index].status = action.status;

      if (this.deletionActions.length && this.deletionActions.every((action) => action.status === 'done')) {
        this.sourceWebsite = {} as Website;
      }

      if (!action.logs || !action.logs.length) {
        return;
      }

      if (!this.deletionActions[index].logs) {
        this.deletionActions[index].logs = [];
      }

      this.deletionActions[index].logs.push(...action.logs.map((l) => ({
        id: -1,
        log: l,
        createdDate: (new Date()).toISOString(),
      })));
    }
  }

  public async reloadTargetWebsiteData(): Promise<void> {
    try {
      const website = await this.websiteRepository.getWebsiteById(this.targetWebsite.id);
      if (!website) {
        return;
      }

      this.targetWebsite.details.sslCertificateId = website.details.sslCertificateId;
      this.targetWebsite.details.vhostId = website.details.vhostId;
      this.targetWebsite.details.unixUserId = website.details.unixUserId;
      this.targetWebsite.details.databaseIds = website.details.databaseIds;
    } catch (err) {
      console.error(err);
    }
  }

  public showUpdateActionStatusDialog(action: WebsiteMigrationAction, approve: boolean): void {
    let status = OrchestratorActionStatus.DONE;
    if (!approve) {
      status = OrchestratorActionStatus.FAILED;
    }

    this.updateWebsiteActionStatusDialog = 'update-website-action-status-dialog';
    this.actionForUpdateActionStatusDialog = action;
    this.statusForUpdateWebsiteActionStatusDialog = status;
  }

  public async updateActionStatus(actionId: number, status: string): Promise<void> {
    try {
      await this.websiteMigrationsRepository.updateMigrationActionStatus(
        this.migration.id,
        actionId,
        OrchestratorActionType.MIGRATION,
        status as OrchestratorActionStatus,
      );

      this.$successDialog.show('Action Status Update Started!');
    } catch (err) {
      this.$errorDialog.show(
        `Error occurred while trying to update migration action status, ${err?.response?.data?.error || err.message}`,
      );
    }
  }

  public getTypeString(type: string): string {
    return type
      .replaceAll('migrate', '')
      .replaceAll('-', ' ')
      .trim()
      .toUpperCase();
  }

  public actionStatusColor(status: string): string {
    const normalized = (status || '').toLowerCase();
    if (['done', 'active'].includes(normalized)) {
      return 'success';
    }
    if (['failed', 'error'].includes(normalized)) {
      return 'error';
    }
    if (['pending', 'in progress', 'retry', 'retrying', 'rolling back'].includes(normalized)) {
      return 'info';
    }
    if (['cancelled', 'rolled back', 'disabled'].includes(normalized)) {
      return 'orange';
    }
    return 'grey';
  }

  public canRetryAction(action: WebsiteMigrationAction): boolean {
    if (['approve_migration_data', 'approve_website_migration'].includes(action.action)) {
      return false;
    }

    if (action.priority === '1') {
      return false;
    }

    if ([
      WebsiteCreationActionStatus.PENDING,
      WebsiteCreationActionStatus.IN_PROGRESS,
      WebsiteCreationActionStatus.RETRY,
      WebsiteCreationActionStatus.RETRYING,
      WebsiteCreationActionStatus.ROLLING_BACK,
      WebsiteCreationActionStatus.DONE,
      WebsiteCreationActionStatus.CANCELLED,
    ].includes(action.status as WebsiteCreationActionStatus)) {
      return false;
    }

    if (!action.parentActionsIds || !action.parentActionsIds.length) {
      return true;
    }

    return action.parentActionsIds.every((parentActionId) => {
      const parentAction = this.migration.actions.find((action) => action.id === parentActionId);

      return !(!parentAction || parentAction.status !== WebsiteCreationActionStatus.DONE);
    });
  }

  public showRetryDialog(creationId: number, action: WebsiteMigrationAction): void {
    this.retryWebsiteActionDialog = 'retry-website-creation-action-dialog';
    this.domainForRetryWebsiteActionDialog = this.targetWebsite.domain;
    this.creationIdForRetryWebsiteActionDialog = creationId;
    this.actionForRetryWebsiteActionDialog = action;
  }

  public async retryAction(migrationId: number, action: WebsiteMigrationAction): Promise<void> {
    try {
      await this.websiteMigrationsRepository.updateMigrationActionStatus(
        migrationId,
        action.id,
        OrchestratorActionType.MIGRATION,
        OrchestratorActionStatus.RETRY,
      );

      this.migration.status = WebsiteCreationStatus.PENDING;
      this.migration.actions.forEach((m, index) => {
        if (m.id !== action.id) {
          return;
        }

        this.migration.actions[index].status = WebsiteCreationActionStatus.RETRY;
      });

      this.$successDialog.show(`Retry website migration action ${action.action} started`);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public canIgnoreAction(action: WebsiteMigrationAction): boolean {
    if (![
      'migrate_email',
      'migrate_ftp',
      'migrate_letsencrypt',
      'migrate_cdn',
      'migrate_redis_cache',
    ].includes(action.action)) {
      return false;
    }

    return action.status === WebsiteCreationActionStatus.FAILED;
  }

  public showIgnoreDialog(creationId: number, action: WebsiteMigrationAction): void {
    this.ignoreWebsiteActionDialog = 'ignore-website-creation-action-dialog';
    this.domainForIgnoreWebsiteActionDialog = this.targetWebsite.domain;
    this.creationIdForIgnoreWebsiteActionDialog = creationId;
    this.actionForIgnoreWebsiteActionDialog = action;
  }

  public async ignoreAction(migrationId: number, action: WebsiteMigrationAction): Promise<void> {
    try {
      await this.websiteMigrationsRepository.updateMigrationActionStatus(
        migrationId,
        action.id,
        OrchestratorActionType.MIGRATION,
        OrchestratorActionStatus.DONE,
      );

      this.migration.status = WebsiteCreationStatus.PENDING;
      this.migration.actions.forEach((m, index) => {
        if (m.id !== action.id) {
          return;
        }

        this.migration.actions[index].status = WebsiteCreationActionStatus.DONE;
      });

      this.$successDialog.show(`Retry website migration action ${action.action} started`);
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }
}
